/**
 * ── **새 세계 알림** ──
 *
 * 총괄: *"'새 세계가 열렸다' 를 알릴 수단이 없다. **한 번 나간 사람은 안 돌아온다.**"*
 *
 * ── ⚠ 이것이 무엇을 못 하는지 먼저 적는다 ──────────────────────
 *
 * **탭을 닫은 사람에게는 못 보낸다.** 그러려면 서비스 워커 + Web Push(VAPID 키 ·
 * 구독 저장 · 푸시 서버)가 필요하고, 그건 이 파일의 몇 배짜리 작업이다.
 *
 * **이것이 덮는 것은 "탭을 열어둔 채 다른 일을 하는 사람"** 이다 — 세계가 끝나가는
 * 것을 알고 잠깐 자리를 뜬 사람이 그 부류고, 실제로 돌아올 가능성이 제일 높은 쪽이다.
 * ⛔ **"알림 붙였다" 로 뭉뚱그리지 않는다.** 못 닿는 사람이 있다는 것을 적어둬야
 * 다음 사람이 Web Push 를 진짜 필요할 때 만든다.
 *
 * ── 규칙 ───────────────────────────────────────────────────────
 *
 * ⚠ **권한을 먼저 묻지 않는다.** 들어오자마자 브라우저 권한 창이 뜨면 그건
 * 절대규칙 4(*"가입 화면·튜토리얼 팝업을 추가하는 변경은 반려 대상"*)와 같은 부류다 —
 * **사람이 [알려줘] 를 누른 그 순간에만** 묻는다.
 *
 * ⚠ **못 되면 조용히 없던 일로 한다.** 알림은 곁다리다. 권한이 거부됐다고
 * 게임이 막히거나 오류를 뿜으면 그건 개선이 아니라 고장이다(`ads.js` 와 같은 규칙).
 */

const KEY = 'unige_notify';
/** 마지막으로 알린 세계 번호. 같은 세계로 두 번 울리지 않게 */
const KEY_N = 'unige_notify_n';

const get = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const set = (k, v) => { try { localStorage.setItem(k, v); } catch { /* 사생활 모드 */ } };

/**
 * 이 브라우저가 알림을 할 수 있나.
 *
 * ⚠ **`Notification` 을 읽는 것만으로도 던질 수 있다** — 권한 정책(`permissions-policy`)
 * 으로 막힌 iframe 이 그렇다. 그래서 **존재 확인까지 감싼다.**
 */
export const canNotify = () => {
  try { return typeof Notification !== 'undefined'; } catch { return false; }
};

/** 지금 권한. **읽다가 던지면 없는 것으로 친다** */
function perm() {
  try { return canNotify() ? Notification.permission : ''; } catch { return ''; }
}

/** 켜 뒀나 */
export const notifyOn = () => get(KEY) === '1' && perm() === 'granted';

/**
 * 지금 상태를 한 마디로. 화면이 **무엇을 눌러야 하는지** 알려면 이게 필요하다.
 *
 * ★ **이 함수는 그리는 길 위에 있다**(`help.js` 가 매번 부른다). 여기서 던지면
 * **도움말 화면이 통째로 안 열린다** — 곁다리 기능이 본체를 막는 그 부류고,
 * 오늘만 두 번 났다. 이 파일 머리말이 *"못 되면 조용히 없던 일로 한다"* 라고
 * 적어놓고 **정작 제일 자주 불리는 이 함수만 안 지키고 있었다**(독립 검증이 잡았다).
 *
 * @returns {'unsupported'|'denied'|'on'|'off'}
 */
export function notifyState() {
  const p = perm();
  if (!p) return 'unsupported';
  if (p === 'denied') return 'denied';
  return notifyOn() ? 'on' : 'off';
}

/**
 * 켜기 — **사람이 누른 그 순간에만 부른다.**
 * @returns {Promise<'on'|'denied'|'unsupported'>}
 */
export async function enableNotify() {
  // ⚠ 지역 이름을 `perm` 으로 두면 위 `perm()` 함수를 가린다 — 한 글자 차이로
  //    가려진 쪽을 부르는 사고가 이 레포에 이미 있었다
  let p = perm();
  if (!p) return 'unsupported';
  if (p === 'default') {
    try { p = await Notification.requestPermission(); } catch { return 'denied'; }
  }
  if (p !== 'granted') return 'denied';
  set(KEY, '1');
  return 'on';
}

/** 끄기 — **권한은 못 거둔다.** 우리 쪽 표시만 내린다 */
export function disableNotify() { set(KEY, '0'); }

/**
 * **세계가 바뀌었으면 알린다.**
 *
 * ⚠ 판정은 **세계 번호**로 한다. 시간으로 하면 시계가 어긋난 브라우저에서
 * 안 울리거나 두 번 울린다. 번호는 서버가 세는 값이라 갈라지지 않는다.
 *
 * ⚠ **처음 본 번호는 안 알린다.** 지금 하고 있는 세계를 *"새로 열렸다"* 고
 * 알리면 거짓말이다 — 그건 그냥 접속이다.
 */
export function worldChanged(n) {
  if (!n || n <= 0) return false;
  const last = Number(get(KEY_N)) || 0;
  set(KEY_N, String(n));
  if (!last || n <= last) return false;   // 처음 봤거나 되돌아간 것
  if (!notifyOn()) return false;
  if (document.visibilityState === 'visible') return false;  // 보고 있으면 알림이 아니라 방해다
  try {
    new Notification('유니지 — 제 ' + n + ' 세계가 열렸다', {
      body: '순위가 새로 시작됐다. 캐릭터는 이름 그대로 1레벨에서 다시 시작한다. 창고와 새 세계 특전은 입장해 확인해 달라.',
      tag: 'unige-world',   // 여러 번 열려도 알림은 하나로 겹친다
    });
    return true;
  } catch {
    return false;   // 못 띄우면 없던 일로 한다
  }
}
